import React from 'react';
import { ArrowRightIcon, BarChart3Icon, BookOpenIcon, BrainCircuitIcon, CheckCircle2Icon, CircleCheckIcon, Clock3Icon, CompassIcon, CreditCardIcon, DownloadIcon, FileTextIcon, FilterIcon, FlameIcon, FacebookIcon, GemIcon, InstagramIcon, LightbulbIcon, ListChecksIcon, LockKeyholeIcon, MailIcon, MessageCircleIcon, NetworkIcon, ShieldCheckIcon, TargetIcon, UsersRoundIcon, YoutubeIcon, ZapIcon, BoxIcon } from 'lucide-react';
import { MetaPixel } from '../components/MetaPixel';
import { PurchaseButton } from '../components/PurchaseButton';
import { Reveal } from '../components/Reveal';
import { SectionHeading } from '../components/SectionHeading';
const BASE_PRODUCT = 'LÍDER PRIMERO™';
const COMBO_PRODUCT = 'Kit Premium LÍDER PRIMERO™ + TRACCIÓN™';
const BASE_PRICE = 490;
const COMBO_PRICE = 690;
const BASE_CHECKOUT_URL = 'https://www.avanzapro.store/cart/48657441161441:1';
const PREMIUM_CHECKOUT_URL = 'https://www.avanzapro.store/cart/48657442046177:1';
type ContentCard = {
  title: string;
  details: string[];
  icon: BoxIcon;
  tone: string;
};
const tomes: ContentCard[] = [{
  title: 'Mentalidad Primero™',
  details: ['Por qué la mentalidad marca la diferencia entre crecer y estancarse.', 'Cómo identificar los patrones que te frenan.', 'El mapa del networker que construye.'],
  icon: BrainCircuitIcon,
  tone: 'bg-[#0b1f3a]'
}, {
  title: 'El Sistema Operativo Interno™',
  details: ['Las 5 creencias limitantes más comunes en networkers de LATAM.', 'Cómo reescribir las creencias que te frenan.', 'El protocolo de identidad: de empleado a constructor.'],
  icon: CompassIcon,
  tone: 'bg-[#102a56]'
}, {
  title: 'Prospección Sin Miedo™',
  details: ['Por qué el miedo a prospectar no es técnico, sino de interpretación.', '4 filtros para priorizar contactos.', 'Cómo responder al sí, la duda y el no.'],
  icon: MessageCircleIcon,
  tone: 'bg-[#123b74]'
}, {
  title: 'Construir el Equipo™',
  details: ['Sistemas de duplicación sin depender de tu presencia constante.', 'El protocolo de activación de socios en las primeras 24 horas.', 'Por qué motivar no es liderar — y qué hacer en cambio.'],
  icon: UsersRoundIcon,
  tone: 'bg-[#172554]'
}, {
  title: 'Liderazgo Primero™',
  details: ['El Protocolo C.O.N.F.I.A.N.Z.A™.', 'Cómo desarrollar líderes y no solo seguidores.', 'Tu identidad como Líder Primero.'],
  icon: NetworkIcon,
  tone: 'bg-[#1e3a8a]'
}];
const tractionModules: ContentCard[] = [{
  title: 'El Diagnóstico',
  details: ['La diferencia entre estar ocupado y ser productivo.', 'Las 4 Zonas de Energía del Networker.', 'Audit-Sheet de Actividades para descubrir dónde se va tu tiempo.'],
  icon: BarChart3Icon,
  tone: 'bg-[#0b1f3a]'
}, {
  title: 'El Enfoque',
  details: ['Las 3 acciones que generan el 80% del volumen.', 'Prospectar, presentar y cerrar: mínimos diarios de TRACCIÓN™.', 'La regla de las 3 preguntas antes del mantenimiento.'],
  icon: TargetIcon,
  tone: 'bg-[#102a56]'
}, {
  title: 'La Cirugía',
  details: ['El Filtro de Pareto para prospectos.', 'El Filtro de Pareto para tu equipo.', 'Cómo dejar de invertir energía en lo que no multiplica.'],
  icon: FilterIcon,
  tone: 'bg-[#123b74]'
}, {
  title: 'La Implementación',
  details: ['El Bloque de Poder: 60 minutos que cambian todo.', 'Cómo estructurar tu bloque para generar máximo resultado.', 'Tu Agenda Semanal de TRACCIÓN™ lista para aplicar.'],
  icon: Clock3Icon,
  tone: 'bg-[#172554]'
}, {
  title: 'La Identidad',
  details: ['El Manifiesto del Networker de TRACCIÓN™.', 'La declaración que separa al ocupado del productivo.', 'Tu compromiso de enfoque radical.'],
  icon: ZapIcon,
  tone: 'bg-[#1e3a8a]'
}];
const faqs = [{
  question: '¿Qué incluye LÍDER PRIMERO™?',
  answer: 'Incluye 5 tomos en PDF de 20 páginas cada uno y 5 bonos prácticos descargables. Tendrás acceso inmediato a todo el material después de la compra.'
}, {
  question: '¿Qué cambia al elegir el Kit Premium?',
  answer: 'Además de LÍDER PRIMERO™, recibís TRACCIÓN™ completo: 5 módulos, el Audit-Sheet de Actividades, el Tablero Semanal y la Revisión Semanal de TRACCIÓN™.'
}, {
  question: '¿Cómo recibo el material?',
  answer: 'Una vez confirmado el pago, recibís por email los enlaces para descargar todos los PDFs en tu celular, tablet o computadora.'
}, {
  question: '¿Es un pago único?',
  answer: 'Sí. Tanto LÍDER PRIMERO™ como el Kit Premium son pagos únicos. No incluyen mensualidades ni suscripciones.'
}, {
  question: '¿Tiene garantía?',
  answer: 'Sí. Contás con una garantía incondicional de 7 días. Si el material no era lo que esperabas, podés solicitar el reintegro del 100% de tu inversión.'
}];
function PaymentNote({
  inverted = false
}: {
  inverted?: boolean;
}) {
  return <div className={`mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs font-medium ${inverted ? 'text-emerald-50/75' : 'text-slate-500'}`} data-id="element-22">
      <span className="inline-flex items-center gap-1.5" data-id="element-23">
        <LockKeyholeIcon size={13} data-id="element-24" /> Pago seguro
      </span>
      <span className="inline-flex items-center gap-1.5" data-id="element-25">
        <FileTextIcon size={13} data-id="element-26" /> 100% PDF
      </span>
      <span className="inline-flex items-center gap-1.5" data-id="element-27">
        <ShieldCheckIcon size={13} data-id="element-28" /> Garantía de 7 días
      </span>
    </div>;
}
function ProductPreview() {
  return <figure className="relative mx-auto w-full max-w-2xl" data-id="element-29">
      <div className="absolute -right-5 top-10 hidden h-40 w-40 rounded-full border border-sky-300/30 lg:block" data-id="element-30" />
      <div className="relative overflow-hidden rounded-3xl border border-blue-500/50 bg-[#0b1f3a] p-2 shadow-2xl shadow-black/40 sm:p-3" data-id="element-31">
        <img src="https://cdn.magicpatterns.com/uploads/wkHfvWRPjsJ7VXAMn2Jj1A/heylloyd-generated-1784083855110.png" alt="Mockup de LÍDER PRIMERO™ con seis tomos y cinco bonos descargables" width={1200} height={800} loading="eager" fetchPriority="high" decoding="async" sizes="(min-width: 1024px) 520px, (min-width: 640px) 560px, calc(100vw - 40px)" className="aspect-[3/2] w-full rounded-2xl object-cover" data-id="element-32" />
      </div>
      <figcaption className="mx-auto mt-3 inline-flex items-center rounded-full border border-sky-300/30 bg-slate-950 px-4 py-2 text-[10px] font-black tracking-[0.14em] text-sky-300" data-id="element-33">
        6 TOMOS + 5 BONOS · ACCESO INMEDIATO
      </figcaption>
    </figure>;
}
function Checklist({
  items,
  dark = false
}: {
  items: string[];
  dark?: boolean;
}) {
  return <ul className={`space-y-3 text-sm leading-6 ${dark ? 'text-emerald-50/85' : 'text-slate-600'}`} data-id="element-34">
      {items.map(item => <li key={item} className="flex gap-3" data-id="element-35">
          <CircleCheckIcon className={`mt-0.5 h-5 w-5 shrink-0 ${dark ? 'text-amber-300' : 'text-emerald-600'}`} aria-hidden="true" data-id="element-36" />
          <span data-id="element-37">{item}</span>
        </li>)}
    </ul>;
}
function ContentGrid({
  items,
  label,
  isTraction = false
}: {
  items: ContentCard[];
  label: string;
  isTraction?: boolean;
}) {
  return <div className="mx-auto mt-8 grid max-w-6xl gap-3 sm:mt-10 sm:gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-38">
      {items.map((item, index) => {
      const Icon = item.icon;
      return <Reveal key={item.title} delay={index * 0.04} data-id="element-39">
            <article className="h-full overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-sm" data-id="element-40">
              <div className={`${item.tone} p-4 text-white sm:p-5`} data-id="element-41">
                <p className="text-[10px] font-black tracking-[0.15em] text-amber-300" data-id="element-42">
                  {label} {index + 1}
                </p>
                <Icon className="mt-3 h-8 w-8 text-emerald-200 sm:mt-4" aria-hidden="true" data-id="element-43" />
                <h3 className="mt-4 text-xl font-black leading-tight sm:mt-5" data-id="element-44">
                  {item.title}
                </h3>
              </div>
              <div className="p-4 sm:p-5" data-id="element-45">
                <ul className="space-y-2.5 text-sm leading-5 text-slate-600 sm:space-y-3" data-id="element-46">
                  {item.details.map(detail => <li key={detail} className="flex gap-2" data-id="element-47">
                      <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" data-id="element-48" />
                      {detail}
                    </li>)}
                </ul>
                <p className="mt-4 border-t border-emerald-100 pt-3 text-xs font-bold text-emerald-700 sm:mt-5 sm:pt-4" data-id="element-49">
                  <FileTextIcon size={13} className="mr-1 inline" aria-hidden="true" data-id="element-50" />{' '}
                  PDF · Acceso inmediato
                </p>
              </div>
            </article>
          </Reveal>;
    })}
      {isTraction && <div className="hidden xl:block" aria-hidden="true" data-id="element-51" />}
    </div>;
}
function OfferCard({
  combo = false
}: {
  combo?: boolean;
}) {
  const productName = combo ? COMBO_PRODUCT : BASE_PRODUCT;
  const price = combo ? COMBO_PRICE : BASE_PRICE;
  const benefits = combo ? ['5 tomos LÍDER PRIMERO™', '5 bonos prácticos', 'TRACCIÓN es el método Premium que te enfocará realmente en tu crecimiento.', '300+ páginas aplicables'] : ['5 tomos LÍDER PRIMERO™', '5 bonos prácticos', '180+ páginas aplicables', 'Acceso inmediato y de por vida'];
  return <article className={`relative h-full rounded-3xl border p-5 sm:p-7 ${combo ? 'border-amber-400 bg-[#062f27] text-white shadow-2xl shadow-emerald-950/20' : 'border-emerald-100 bg-white text-emerald-950 shadow-sm'}`} data-id="element-52">
      {combo && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-amber-400 px-4 py-1 text-[10px] font-black tracking-[0.13em] text-emerald-950" data-id="element-53">
          MÁS RECOMENDADO
        </span>}
      <p className={`text-[10px] font-black tracking-[0.15em] ${combo ? 'text-amber-300' : 'text-emerald-700'}`} data-id="element-54">
        OPCIÓN {combo ? '2' : '1'}
      </p>
      <h3 className="mt-2 text-2xl font-black leading-tight" data-id="element-55">
        {combo ? 'Kit Premium' : 'LÍDER PRIMERO™'}
      </h3>
      <p className={`mt-2 text-sm font-bold ${combo ? 'text-emerald-100/75' : 'text-slate-500'}`} data-id="element-56">
        {combo ? 'LÍDER PRIMERO™ + TRACCIÓN™' : 'La base de tu liderazgo personal'}
      </p>
      <ul className={`mt-5 space-y-2.5 border-y py-4 text-sm sm:mt-6 sm:space-y-3 sm:py-5 ${combo ? 'border-emerald-800 text-emerald-50' : 'border-emerald-100 text-slate-600'}`} data-id="element-57">
        {benefits.map(benefit => <li key={benefit} className="flex gap-2" data-id="element-58">
            <CheckCircle2Icon className={`h-5 w-5 shrink-0 ${combo ? 'text-amber-300' : 'text-emerald-600'}`} data-id="element-59" />
            {benefit}
          </li>)}
      </ul>
      <div className="mt-5 sm:mt-6" data-id="element-60">
        <p className={`text-xs font-black tracking-[0.12em] ${combo ? 'text-emerald-200' : 'text-emerald-700'}`} data-id="element-61">
          INVERSIÓN ÚNICA
        </p>
        <p className={`mt-1 text-5xl font-black tracking-[-0.06em] ${combo ? 'text-amber-300' : 'text-emerald-700'}`} data-id="element-62">
          ${price}
        </p>
        <p className={`text-sm font-bold ${combo ? 'text-emerald-50' : 'text-emerald-950'}`} data-id="element-63">
          pesos uruguayos
        </p>
        {combo && <p className="mt-2 text-xs font-semibold text-emerald-100/75" data-id="element-64">
            TRACCIÓN™ por solo $200 más.
          </p>}
      </div>
      <PurchaseButton productName={productName} value={price} href={combo ? PREMIUM_CHECKOUT_URL : BASE_CHECKOUT_URL} className={`mt-5 w-full sm:mt-6 ${combo ? 'bg-amber-400' : ''}`} data-id="element-65">
        {combo ? 'QUIERO ESTE SISTEMA AHORA' : 'QUIERO SOLO LÍDER PRIMERO™'}
      </PurchaseButton>
      <PaymentNote inverted={combo} data-id="element-66" />
    </article>;
}
export function LandingPage() {
  return <main className="w-full overflow-hidden bg-[#f7f5ef] text-[#0b2e27]" data-id="element-67">
      <MetaPixel data-id="element-68" />
      <div className="sticky top-0 z-40 bg-amber-400 px-4 py-2 text-center text-[10px] font-black tracking-wide text-emerald-950 sm:text-[11px]" data-id="element-69">
        HOY PODÉS CONSTRUIR EL LIDERAZGO QUE TU EQUIPO NECESITA
      </div>

      <section className="bg-[#062f27] px-5 pb-12 pt-5 text-white sm:px-8 sm:pb-16 lg:pb-20" data-id="element-70">
        <nav aria-label="Navegación principal" className="mx-auto flex max-w-6xl items-center justify-between" data-id="element-71">
          <a href="#inicio" className="leading-tight" data-id="element-72">
            <span className="block text-sm font-black tracking-[0.16em] text-amber-300" data-id="element-73">
              AVANZA PRO™
            </span>
            <span className="block text-[10px] font-semibold text-emerald-100/70" data-id="element-74">
              La Facultad del Nuevo Mundo
            </span>
          </a>
          <a href="#ofertas" className="text-xs font-bold text-emerald-100 transition hover:text-amber-300" data-id="element-75">
            Ver opciones
          </a>
        </nav>

        <div id="inicio" className="mx-auto grid max-w-6xl items-center gap-8 pt-8 lg:grid-cols-[1.05fr_.95fr] lg:gap-10 lg:pt-12" data-id="element-76">
          <Reveal data-id="element-77">
            <p className="eyebrow bg-emerald-400/15 text-emerald-200" data-id="element-78">
              PARA NETWORKERS DE TODO LATINOAMERICA
            </p>
            <h1 className="mt-4 max-w-2xl text-4xl font-black leading-[0.98] tracking-[-0.06em] sm:text-5xl lg:text-6xl" data-id="element-79">
              Tenés equipo. Trabajás todos los días.{' '}
              <span className="text-amber-300" data-id="element-80">Y el negocio no crece.</span>
            </h1>
            <p className="mt-4 max-w-xl text-[15px] leading-6 text-emerald-50/85 sm:text-base sm:leading-7" data-id="element-81">
              Para los networkers que ya saben que el problema no es el negocio.
              El problema es lo que nadie te enseñó sobre liderarte primero a
              vos mismo.
            </p>
            <p className="mt-3 max-w-xl text-sm leading-6 text-emerald-100/65" data-id="element-82">
              Descubrí LÍDER PRIMERO™: el sistema visual para construir un
              equipo que se mueve con estructura, sin depender de la motivación
              del momento ni de que alguien más te salve.
            </p>
          </Reveal>
          <Reveal delay={0.1} data-id="element-83">
            <div className="text-center" data-id="element-84">
              <ProductPreview data-id="element-85" />
              <div className="mt-5" data-id="element-86">
                <PurchaseButton productName={COMBO_PRODUCT} value={COMBO_PRICE} href="#ofertas" trackCheckout={false} data-id="element-87">
                  QUIERO ESTE SISTEMA AHORA
                </PurchaseButton>
                <PaymentNote inverted data-id="element-88" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-shell bg-[#edf5f0]" data-id="element-89">
        <div className="mx-auto max-w-4xl" data-id="element-90">
          <Reveal data-id="element-91">
            <SectionHeading eyebrow="ESTO TE VA A SONAR" title="Hay un momento que casi todos los networkers conocen." description="No es cuando entrás al negocio. Es cuando mirás tu equipo, ves personas que no accionan y te preguntás por qué seguís construyendo solo." data-id="element-92" />
          </Reveal>
          <Reveal delay={0.06} data-id="element-93">
            <div className="mx-auto mt-10 max-w-3xl rounded-3xl border border-emerald-100 bg-white p-7 shadow-sm sm:p-10" data-id="element-94">
              <p className="text-lg font-black leading-tight text-emerald-950 sm:text-2xl" data-id="element-95">
                “¿Por qué yo sigo construyendo solo si se supone que tengo un
                equipo?”
              </p>
              <p className="mt-5 text-sm leading-6 text-slate-600" data-id="element-96">
                El grupo está lleno de mensajes y vacío de resultados. Vos
                seguís prospectando, presentando y sosteniendo el volumen. Si
                alguna vez sentiste eso, esta propuesta fue creada para vos.
              </p>
              <div className="mt-7 border-l-4 border-amber-400 pl-5" data-id="element-97">
                <p className="text-base font-black leading-6 text-emerald-900" data-id="element-98">
                  Tu equipo no crece porque todavía no sos el líder que ese
                  equipo necesita.
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-99">
                  No porque seas malo ni porque el negocio no funcione. Porque
                  nadie te enseñó a construirte como líder antes de construir el
                  equipo.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-shell bg-white" data-id="element-100">
        <Reveal data-id="element-101">
          <SectionHeading eyebrow="¿TE IDENTIFICÁS?" title="No estás solo: esto es lo que pasa cuando no hay sistema." data-id="element-102" />
        </Reveal>
        <Reveal delay={0.05} data-id="element-103">
          <div className="mx-auto mt-10 max-w-4xl rounded-3xl bg-[#fbefea] p-6 sm:p-8" data-id="element-104">
            <Checklist items={['Tenés personas que dijeron que iban a construir y no hicieron nada.', 'Sos quien prospecta, presenta y sostiene el volumen.', 'Cuando motivás al equipo funciona 48 horas y después vuelve al mismo lugar.', 'Trabajás mucho, pero el cheque no refleja el esfuerzo.', 'Evitás contactar a ciertas personas aunque sabés que deberías hacerlo.', 'Un no tarda días en dejar de afectarte.', 'No tenés un sistema claro para activar a alguien nuevo en las primeras 24 horas.']} data-id="element-105" />
            <p className="mt-7 border-t border-[#efd1c8] pt-6 text-center text-lg font-black text-[#5a2d28]" data-id="element-106">
              El problema no es tu negocio. Nadie te entrenó para ser el líder
              que ese negocio necesita.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="section-shell bg-[#eaf2ec]" data-id="element-107">
        <Reveal data-id="element-108">
          <SectionHeading eyebrow="POR QUÉ LO DE SIEMPRE FALLA" title="La motivación no sustituye un sistema de liderazgo." data-id="element-109" />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-6xl gap-4 md:grid-cols-2" data-id="element-110">
          {[['Eventos de la empresa', 'Generan motivación de corto plazo. Sin un sistema, el lunes siguiente todo vuelve a ser igual.', FlameIcon], ['Libros de liderazgo', 'Suelen estar escritos para ejecutivos, no para quien prospecta por WhatsApp después de trabajar.', BookOpenIcon], ['El consejo del upline', '“Prospectá más” es una instrucción sin estructura. Te dice qué hacer, no cómo construirte para hacerlo.', UsersRoundIcon], ['Cursos genéricos de ventas', 'Enseñan guiones y cierres, pero sin la mentalidad correcta las técnicas suenan falsas.', MessageCircleIcon]].map(([title, copy, Icon]) => {
          const CardIcon = Icon as BoxIcon;
          return <Reveal key={String(title)} data-id="element-111">
                <article className="h-full rounded-2xl border border-emerald-100 bg-white p-6 shadow-sm" data-id="element-112">
                  <CardIcon className="h-9 w-9 rounded-xl bg-[#fce8df] p-2 text-[#b65347]" data-id="element-113" />
                  <h3 className="mt-5 text-lg font-black" data-id="element-114">{String(title)}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-115">
                    {String(copy)}
                  </p>
                </article>
              </Reveal>;
        })}
        </div>
      </section>

      <section id="lider-primero" className="section-shell bg-[#062f27] text-white" data-id="element-116">
        <Reveal data-id="element-117">
          <SectionHeading inverted eyebrow="LA SOLUCIÓN" title="LÍDER PRIMERO™" description="Sistema visual de entrenamiento y transformación para líderes de network marketing." data-id="element-118" />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-5xl gap-7 lg:grid-cols-2" data-id="element-119">
          <Reveal data-id="element-120">
            <div className="rounded-3xl border border-emerald-800 bg-emerald-900/70 p-7" data-id="element-121">
              <p className="text-xl font-black leading-tight text-amber-300" data-id="element-122">
                Convertite en el tipo de líder que otros eligen seguir
                voluntariamente.
              </p>
              <p className="mt-4 text-sm leading-6 text-emerald-100/75" data-id="element-123">
                Sin depender de la motivación del momento. Sin esperar que el
                upline te diga qué hacer. Con un sistema propio que funciona
                aunque el equipo no se mueva.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.06} data-id="element-124">
            <Checklist dark items={['Construí una mentalidad de constructor.', 'Ordená tu sistema operativo interno.', 'Prospectá sin miedo y con criterio.', 'Creá duplicación y activación real en tu equipo.', 'Desarrollá líderes, no seguidores.']} data-id="element-125" />
          </Reveal>
        </div>
      </section>

      <section className="section-shell bg-white" data-id="element-126">
        <Reveal data-id="element-127">
          <SectionHeading eyebrow="EL SISTEMA COMPLETO" title="5 tomos para liderarte primero y construir mejor después." description="Cada tomo incluye ejercicios de aplicación inmediata. PDF de 20 páginas con acceso inmediato." data-id="element-128" />
        </Reveal>
        <ContentGrid items={tomes} label="TOMO" data-id="element-129" />
        <div className="mt-9 text-center" data-id="element-130">
          <PurchaseButton productName={BASE_PRODUCT} value={BASE_PRICE} href={BASE_CHECKOUT_URL} data-id="element-131">
            QUIERO LÍDER PRIMERO™ POR $490
          </PurchaseButton>
        </div>
      </section>

      <section className="section-shell bg-[#fff8e8]" data-id="element-132">
        <Reveal data-id="element-133">
          <article className="mx-auto max-w-5xl rounded-3xl border border-amber-300 bg-white p-7 shadow-sm sm:p-9" data-id="element-134">
            <div className="grid gap-6 md:grid-cols-[auto_1fr]" data-id="element-135">
              <div className="grid h-16 w-16 place-items-center rounded-2xl bg-amber-400 text-emerald-950" data-id="element-136">
                <GemIcon size={30} data-id="element-137" />
              </div>
              <div data-id="element-138">
                <p className="text-xs font-black tracking-[0.14em] text-amber-700" data-id="element-139">
                  5 BONOS PRÁCTICOS · INCLUIDOS
                </p>
                <h2 className="mt-2 text-2xl font-black" data-id="element-140">
                  No solo leés. Aplicás.
                </h2>
                <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-141">
                  {['Mapa Visual de Prospección Sin Miedo™', 'Checklist de Activación en 24 horas™', 'Guía de Recuperación Emocional™', 'Plantillas de Seguimiento™', 'Tracker de Hábitos 90 Días™'].map((bonus, index) => <div key={bonus} className="rounded-xl bg-amber-50 p-4" data-id="element-142">
                      <p className="text-[10px] font-black tracking-[0.12em] text-amber-700" data-id="element-143">
                        BONO {index + 1}
                      </p>
                      <p className="mt-2 text-sm font-black leading-5" data-id="element-144">
                        {bonus}
                      </p>
                      <p className="mt-2 text-xs text-slate-500" data-id="element-145">
                        PDF descargable · Incluido sin costo extra
                      </p>
                    </div>)}
                </div>
              </div>
            </div>
          </article>
        </Reveal>
      </section>

      <section className="section-shell bg-[#eaf2ec]" data-id="element-146">
        <div className="mx-auto max-w-4xl" data-id="element-147">
          <Reveal data-id="element-148">
            <SectionHeading eyebrow="EL SIGUIENTE NIVEL" title="Más mentalidad sin enfoque correcto solo produce más agotamiento." description="Cuando un networker empieza a construir con más energía, puede intentar hacerlo todo: responder cada mensaje, ir a todos los Zooms, crear contenido y motivar a quienes no accionan. Al final del día queda poco para las acciones que realmente generan movimiento." data-id="element-149" />
          </Reveal>
          <Reveal delay={0.06} data-id="element-150">
            <div className="mt-10 rounded-3xl bg-[#062f27] p-7 text-white sm:p-10" data-id="element-151">
              <p className="text-[10px] font-black tracking-[0.16em] text-amber-300" data-id="element-152">
                POR ESO EXISTE
              </p>
              <h2 className="mt-3 text-4xl font-black tracking-[-0.06em] text-amber-300" data-id="element-153">
                TRACCIÓN™
              </h2>
              <p className="mt-3 text-xl font-black leading-tight" data-id="element-154">
                El sistema 80/20 para líderes de network marketing.
              </p>
              <p className="mt-4 max-w-2xl text-sm leading-6 text-emerald-100/75" data-id="element-155">
                Enfocá tu energía en las acciones que multiplican resultados y
                eliminá el ruido que te agota sin hacer crecer el negocio.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="traccion" className="section-shell bg-white" data-id="element-156">
        <Reveal data-id="element-157">
          <SectionHeading eyebrow="TRACCIÓN™ COMPLETO" title="5 módulos para pasar de ocupado a productivo." description="Un sistema de foco, diagnóstico y ejecución para decidir dónde invertir cada hora de trabajo." data-id="element-158" />
        </Reveal>
        <ContentGrid items={tractionModules} label="MÓDULO" isTraction data-id="element-159" />
        <Reveal data-id="element-160">
          <div className="mx-auto mt-10 max-w-5xl rounded-3xl border border-emerald-100 bg-[#edf5f0] p-6 sm:p-8" data-id="element-161">
            <div className="grid gap-5 md:grid-cols-3" data-id="element-162">
              {[['Audit-Sheet de Actividades', 'Clasificá cada tarea por zona y decidí qué eliminar, reducir o mantener.', BarChart3Icon], ['Tablero Semanal de TRACCIÓN™', 'Registrá tus Bloques de Poder, prospectos y seguimientos de la semana.', ListChecksIcon], ['Revisión Semanal de TRACCIÓN™', 'Analizá qué funcionó, qué no y qué ajustás la semana siguiente.', LightbulbIcon]].map(([title, copy, Icon]) => {
              const ToolIcon = Icon as BoxIcon;
              return <article key={String(title)} className="rounded-2xl bg-white p-5 shadow-sm" data-id="element-163">
                    <ToolIcon className="h-7 w-7 text-emerald-600" data-id="element-164" />
                    <h3 className="mt-4 text-base font-black" data-id="element-165">
                      {String(title)}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-166">
                      {String(copy)}
                    </p>
                  </article>;
            })}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="section-shell bg-[#062f27] text-white" data-id="element-167">
        <Reveal data-id="element-168">
          <SectionHeading inverted eyebrow="KIT PREMIUM" title="El líder correcto haciendo las acciones correctas." data-id="element-169" />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-5xl gap-5 md:grid-cols-3" data-id="element-170">
          {[['LÍDER PRIMERO™', 'Mentalidad, identidad y sistema interno para construirte como líder.', BrainCircuitIcon], ['TRACCIÓN™', 'Enfoque quirúrgico para dirigir tu energía a lo que multiplica.', TargetIcon], ['JUNTOS', 'Mentalidad de constructor + enfoque de élite = crecimiento con sistema.', ZapIcon]].map(([title, copy, Icon], index) => {
          const ItemIcon = Icon as BoxIcon;
          return <Reveal key={String(title)} delay={index * 0.05} data-id="element-171">
                <article className={`h-full rounded-2xl p-6 ${index === 2 ? 'bg-amber-400 text-emerald-950' : 'bg-emerald-900 text-white'}`} data-id="element-172">
                  <ItemIcon className={`h-9 w-9 ${index === 2 ? 'text-emerald-800' : 'text-emerald-300'}`} data-id="element-173" />
                  <h3 className="mt-5 text-xl font-black" data-id="element-174">{String(title)}</h3>
                  <p className={`mt-3 text-sm leading-6 ${index === 2 ? 'text-emerald-900' : 'text-emerald-100/75'}`} data-id="element-175">
                    {String(copy)}
                  </p>
                </article>
              </Reveal>;
        })}
        </div>
      </section>

      <section id="ofertas" className="section-shell bg-[#edf5f0]" data-id="element-176">
        <Reveal data-id="element-177">
          <SectionHeading eyebrow="ELEGÍ TU OPCIÓN" title="Dos caminos. Un mismo objetivo: liderar y avanzar con sistema." data-id="element-178" />
        </Reveal>
        <div className="mx-auto mt-12 grid max-w-5xl items-stretch gap-6 lg:grid-cols-2" data-id="element-179">
          <Reveal data-id="element-180">
            <OfferCard data-id="element-181" />
          </Reveal>
          <Reveal delay={0.06} data-id="element-182">
            <OfferCard combo data-id="element-183" />
          </Reveal>
        </div>
        <p className="mx-auto mt-8 max-w-2xl text-center text-sm leading-6 text-slate-600" data-id="element-184">
          La mentalidad sin enfoque es potencia sin dirección. El Kit Premium
          une las dos piezas para que tu energía nueva vaya exactamente donde el
          negocio crece.
        </p>
      </section>

      <section className="section-shell bg-white" data-id="element-185">
        <Reveal data-id="element-186">
          <SectionHeading eyebrow="EL DIFERENCIAL" title="No es más contenido. Es un sistema pensado para tu realidad." data-id="element-187" />
        </Reveal>
        <div className="mx-auto mt-10 max-w-5xl overflow-hidden rounded-2xl border border-emerald-100" data-id="element-188">
          <div className="grid grid-cols-2 bg-[#062f27] text-xs font-black tracking-wide text-white" data-id="element-189">
            <div className="p-4 text-white/65" data-id="element-190">LO QUE EXISTE HOY</div>
            <div className="bg-emerald-700 p-4" data-id="element-191">KIT PREMIUM</div>
          </div>
          {[['Técnicas sin mentalidad.', 'Mentalidad + técnica en un solo sistema.'], ['Motivación que dura 48 horas.', 'Sistema aplicable sin motivación perfecta.'], ['Contenido genérico para cualquier negocio.', 'Diseñado para la realidad del networker latinoamericano.'], ['Solo habla de técnicas de venta.', 'Empieza por el líder y termina en el enfoque de élite.'], ['No enseña dónde enfocar la energía.', 'Sistema 80/20 aplicado al network marketing real.']].map(([oldWay, newWay]) => <div key={oldWay} className="grid grid-cols-2 border-t border-emerald-100 text-xs sm:text-sm" data-id="element-192">
              <div className="p-4 text-slate-500" data-id="element-193">{oldWay}</div>
              <div className="bg-emerald-50 p-4 font-bold text-emerald-900" data-id="element-194">
                {newWay}
              </div>
            </div>)}
        </div>
      </section>

      <section className="section-shell bg-[#fff8e8]" data-id="element-195">
        <Reveal data-id="element-196">
          <div className="mx-auto max-w-5xl rounded-3xl border border-amber-300 bg-white p-7 text-center shadow-sm sm:p-10" data-id="element-197">
            <ShieldCheckIcon className="mx-auto h-12 w-12 text-amber-500" data-id="element-198" />
            <p className="mt-4 text-xs font-black tracking-[0.15em] text-amber-700" data-id="element-199">
              GARANTÍA INCONDICIONAL DE 7 DÍAS
            </p>
            <h2 className="mt-2 text-3xl font-black" data-id="element-200">
              El riesgo es nuestro. No el tuyo.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-6 text-slate-600" data-id="element-201">
              Aplica tanto para LÍDER PRIMERO™ como para el Kit Premium. Si
              dentro de los primeros 7 días sentís que el material no era lo que
              esperabas, te devolvemos el 100% de tu dinero.
            </p>
            <div className="mt-8 grid gap-5 sm:grid-cols-3" data-id="element-202">
              {[['7 días completos', 'Tiempo para recorrer el sistema con calma.'], ['100% de tu dinero', 'Tu inversión completa si no te convence.'], ['Sin preguntas', 'No tenés que justificar absolutamente nada.']].map(([title, copy]) => <div key={title} data-id="element-203">
                  <ShieldCheckIcon className="mx-auto h-6 w-6 text-amber-500" data-id="element-204" />
                  <h3 className="mt-2 font-black" data-id="element-205">{title}</h3>
                  <p className="mt-1 text-sm text-slate-600" data-id="element-206">{copy}</p>
                </div>)}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="section-shell bg-[#062f27] text-white" data-id="element-207">
        <Reveal data-id="element-208">
          <SectionHeading inverted eyebrow="CÓMO RECIBÍS TU MATERIAL" title="Tres pasos. Sin complicaciones." data-id="element-209" />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-5xl gap-5 md:grid-cols-3" data-id="element-210">
          {[['PASO 1', 'Elegí tu opción y hacé tu pago', 'LÍDER PRIMERO™ a $490 o Kit Premium a $690. Pago seguro desde tu celular o computadora.', CreditCardIcon], ['PASO 2', 'Recibí tu email al instante', 'Después del pago recibís todos los enlaces de descarga directamente en tu correo.', MailIcon], ['PASO 3', 'Descargá y empezá hoy', 'Guardá los PDFs en tu dispositivo y tenelos disponibles para siempre.', DownloadIcon]].map(([step, title, copy, Icon]) => {
          const StepIcon = Icon as BoxIcon;
          return <article key={String(step)} className="rounded-2xl bg-emerald-900 p-6" data-id="element-211">
                <p className="text-[10px] font-black tracking-[0.15em] text-amber-300" data-id="element-212">
                  {String(step)}
                </p>
                <StepIcon className="mt-4 h-9 w-9 text-emerald-300" data-id="element-213" />
                <h3 className="mt-5 text-xl font-black" data-id="element-214">{String(title)}</h3>
                <p className="mt-3 text-sm leading-6 text-emerald-100/75" data-id="element-215">
                  {String(copy)}
                </p>
              </article>;
        })}
        </div>
      </section>

      <section className="section-shell bg-emerald-700 text-center text-white" data-id="element-216">
        <Reveal data-id="element-217">
          <div className="mx-auto max-w-3xl" data-id="element-218">
            <p className="text-xs font-black tracking-[0.15em] text-amber-300" data-id="element-219">
              LÍDER PRIMERO™ + TRACCIÓN™
            </p>
            <h2 className="mt-4 text-3xl font-black leading-tight sm:text-4xl" data-id="element-220">
              Tu equipo actual refleja el líder que sos hoy.
            </h2>
            <p className="mt-4 text-sm leading-6 text-emerald-50/85" data-id="element-221">
              La próxima vez que mires tu equipo y nadie se mueva, podés repetir
              el ciclo. O podés construirte como el líder correcto y enfocar esa
              energía en las acciones correctas.
            </p>
            <p className="mt-7 text-4xl font-black text-amber-300" data-id="element-222">
              $690 <span className="text-base text-white" data-id="element-223">pesos uruguayos</span>
            </p>
            <p className="mt-2 text-sm font-semibold text-emerald-100" data-id="element-224">
              El Kit Premium completo · 300+ páginas aplicables
            </p>
            <PurchaseButton productName={COMBO_PRODUCT} value={COMBO_PRICE} href={PREMIUM_CHECKOUT_URL} className="mt-7" data-id="element-225">
              QUIERO EL KIT PREMIUM POR $690
            </PurchaseButton>
            <div className="mt-5" data-id="element-226">
              <a href="#ofertas" className="text-sm font-bold text-emerald-50 underline underline-offset-4 hover:text-amber-300" data-id="element-227">
                O prefiero solo LÍDER PRIMERO™ por $490
              </a>
            </div>
            <PaymentNote inverted data-id="element-228" />
          </div>
        </Reveal>
      </section>

      <section className="bg-[#062f27] px-5 py-9 text-center text-emerald-50 sm:px-8 sm:py-12" data-id="element-229">
        <blockquote className="mx-auto max-w-3xl text-lg font-bold leading-7" data-id="element-230">
          “Si querés un equipo diferente, primero sé un líder diferente. Y
          después enfocá esa energía exactamente donde el dinero crece.”
        </blockquote>
        <p className="mt-4 text-sm font-black tracking-[0.14em] text-amber-300 sm:mt-5" data-id="element-231">
          AVANZA PRO™
        </p>
      </section>

      <footer className="bg-[#031f1a] px-5 py-6 text-center text-xs leading-6 text-emerald-100/65 sm:py-8" data-id="element-232">
        <a href="https://www.avanzapro.store" className="inline-block font-bold text-emerald-100/80 underline underline-offset-2" data-id="element-233">
          www.avanzapro.store
        </a>
        <nav aria-label="Redes sociales de Avanza Pro" className="mt-4 flex justify-center gap-3 sm:mt-5" data-id="element-234">
          <a href="https://www.instagram.com/avanzapro_oficial/" target="_blank" rel="noreferrer" aria-label="Seguir a Avanza Pro en Instagram" className="grid h-10 w-10 place-items-center rounded-full border border-sky-300/30 text-sky-300 transition hover:border-sky-300 hover:bg-blue-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-sky-300" data-id="element-235">
            <InstagramIcon size={18} aria-hidden="true" data-id="element-236" />
          </a>
          <a href="https://www.facebook.com/profile.php?id=61590326735050" target="_blank" rel="noreferrer" aria-label="Seguir a Avanza Pro en Facebook" className="grid h-10 w-10 place-items-center rounded-full border border-sky-300/30 text-sky-300 transition hover:border-sky-300 hover:bg-blue-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-sky-300" data-id="element-237">
            <FacebookIcon size={18} aria-hidden="true" data-id="element-238" />
          </a>
          <a href="https://www.youtube.com/@avanzapro_ok" target="_blank" rel="noreferrer" aria-label="Suscribirse al canal de YouTube de Avanza Pro" className="grid h-10 w-10 place-items-center rounded-full border border-sky-300/30 text-sky-300 transition hover:border-sky-300 hover:bg-blue-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-sky-300" data-id="element-239">
            <YoutubeIcon size={19} aria-hidden="true" data-id="element-240" />
          </a>
        </nav>
        <a href="https://drive.google.com/file/d/1get8d3Gz1tckuwidvBmreEazNpLa0zav/view?usp=sharing" target="_blank" rel="noreferrer" className="mt-4 inline-flex min-h-11 items-center rounded-full border border-sky-300/40 px-4 py-2 text-xs font-black tracking-wide text-sky-200 transition hover:border-sky-300 hover:bg-blue-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-sky-300 sm:mt-5" data-id="element-241">
          TÉRMINOS Y CONDICIONES
        </a>
        <p className="mt-4 sm:mt-5" data-id="element-242">
          Pago seguro · Producto 100% digital en PDF · Sin envíos físicos
        </p>
        <p data-id="element-243">Acceso inmediato · Garantía incondicional de 7 días</p>
        <p className="mt-4 sm:mt-5" data-id="element-244">
          © 2026 Avanza Pro — Todos los derechos reservados.
        </p>
      </footer>
    </main>;
}